import { useEffect, useCallback } from 'react'

/**
 * Hook to detect virtual keyboard height and update CSS custom properties
 * Provides fallback for browsers that don't support env(keyboard-inset-height)
 */
export const useKeyboardHeight = () => {
  const updateKeyboardHeight = useCallback(() => {
    // Check if browser supports Visual Viewport API
    if (!window.visualViewport) {
      return
    }

    const viewport = window.visualViewport
    const documentHeight = document.documentElement.clientHeight
    
    // Calculate keyboard height
    // When keyboard appears, visualViewport.height shrinks
    const keyboardHeight = Math.max(0, documentHeight - viewport.height)
    
    // Update CSS custom properties for fallback browsers
    document.documentElement.style.setProperty(
      '--keyboard-height', 
      `${keyboardHeight}px`
    )
    
    // Update available height for modal sizing
    const availableHeight = documentHeight - keyboardHeight
    document.documentElement.style.setProperty(
      '--available-height',
      `${availableHeight}px`
    )
    
    // Optional: Dispatch custom event for components that need to react
    window.dispatchEvent(new CustomEvent('keyboardheightchange', {
      detail: { keyboardHeight, isKeyboardVisible: keyboardHeight > 0 }
    }))
  }, [])

  const handleViewportChange = useCallback(() => {
    // Debounce rapid viewport changes
    requestAnimationFrame(updateKeyboardHeight)
  }, [updateKeyboardHeight])

  useEffect(() => {
    // Check for Visual Viewport API support
    if (!window.visualViewport) {
      console.info('Visual Viewport API not supported - keyboard detection limited')
      return
    }

    // Set initial keyboard height
    updateKeyboardHeight()

    // Listen for viewport changes (keyboard show/hide, orientation change)
    window.visualViewport.addEventListener('resize', handleViewportChange)
    window.visualViewport.addEventListener('scroll', handleViewportChange)

    // Cleanup
    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleViewportChange)
        window.visualViewport.removeEventListener('scroll', handleViewportChange)
      }
    }
  }, [handleViewportChange, updateKeyboardHeight])

  // Alternative detection method for older browsers
  useEffect(() => {
    if (window.visualViewport) {
      return // Skip if Visual Viewport API is available
    }

    let initialViewportHeight = window.innerHeight

    const handleResize = () => {
      const currentHeight = window.innerHeight
      const heightDifference = initialViewportHeight - currentHeight
      
      // Assume keyboard is visible if height decreased by more than 150px
      const keyboardHeight = heightDifference > 150 ? heightDifference : 0
      
      document.documentElement.style.setProperty(
        '--keyboard-height', 
        `${keyboardHeight}px`
      )
      
      // Update available height for modal sizing
      const availableHeight = initialViewportHeight - keyboardHeight
      document.documentElement.style.setProperty(
        '--available-height',
        `${availableHeight}px`
      )

      window.dispatchEvent(new CustomEvent('keyboardheightchange', {
        detail: { keyboardHeight, isKeyboardVisible: keyboardHeight > 0 }
      }))
    }

    // Debounced resize handler
    let resizeTimeout
    const debouncedResize = () => {
      clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(handleResize, 100)
    }

    window.addEventListener('resize', debouncedResize)
    window.addEventListener('orientationchange', () => {
      // Reset initial height on orientation change
      setTimeout(() => {
        initialViewportHeight = window.innerHeight
        handleResize()
      }, 500)
    })

    return () => {
      window.removeEventListener('resize', debouncedResize)
      clearTimeout(resizeTimeout)
    }
  }, [])
}